import { useQuery } from "@tanstack/react-query";
import {
	UTILITY_CONSUMPTION_REPORT_MODE,
	UTILITY_CONSUMPTION_UTILITY,
	UtilityConsumptionReportMode,
	UtilityConsumptionReportRange,
	UtilityConsumptionUtility,
} from "../models/UtilityConsumption";
import { getUtilityConsumptionReport } from "../repositories/ConsumptionRepository";
import {
	getUtilityConsumptionReportDateError,
	getUtilityConsumptionReportPeriod,
} from "../utils/calendar";

const REPORT_QUERY_ROOT = "utility-consumption-report";
const ALL_METERS_KEY = ["all-meters"] as const;

export interface UseUtilityConsumptionReportInput {
	targetId: number | null;
	utility: UtilityConsumptionUtility;
	externalMeterIds: string[] | null;
	areMetersLoaded: boolean;
	mode: UtilityConsumptionReportMode;
	sectionId: number | null;
	referenceDate: Date;
	sectionEndBoundary: Date;
	range: UtilityConsumptionReportRange;
	sectionRange: UtilityConsumptionReportRange;
	plantTimeZone: string;
}

export const useUtilityConsumptionReport = ({
	targetId,
	utility,
	externalMeterIds,
	areMetersLoaded,
	mode,
	sectionId,
	referenceDate,
	sectionEndBoundary,
	range,
	sectionRange,
	plantTimeZone,
}: UseUtilityConsumptionReportInput) => {
	const activeRange =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION ? sectionRange : range;
	const dateError =
		mode === UTILITY_CONSUMPTION_REPORT_MODE.PERIOD ||
		mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
			? getUtilityConsumptionReportDateError(activeRange, plantTimeZone)
			: null;
	const canonicalMeterIds =
		externalMeterIds === null
			? null
			: [...new Set(externalMeterIds)].sort((left, right) =>
					left < right ? -1 : left > right ? 1 : 0,
				);
	const meterScopeKey =
		canonicalMeterIds === null
			? ALL_METERS_KEY
			: (["selected-meters", canonicalMeterIds] as const);
	const period =
		dateError === null
			? getUtilityConsumptionReportPeriod(
					mode,
					mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
						? sectionEndBoundary
						: referenceDate,
					activeRange,
					plantTimeZone,
				)
			: null;
	const enabled =
		utility === UTILITY_CONSUMPTION_UTILITY.WATER &&
		areMetersLoaded &&
		dateError === null;
	const query = useQuery({
		queryKey: [
			REPORT_QUERY_ROOT,
			targetId,
			meterScopeKey,
			mode,
			...(mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION
				? [["section-scope", sectionId] as const]
				: []),
			period?.startUtc.toISOString() ?? null,
			period?.endUtc.toISOString() ?? null,
		],
		queryFn: ({ signal }) =>
			getUtilityConsumptionReport(
				{
					targetId,
					externalMeterIds: canonicalMeterIds,
					mode,
					sectionId:
						mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION ? sectionId : null,
					startUtc: period?.startUtc as Date,
					endUtc: period?.endUtc as Date,
				},
				signal,
			),
		enabled,
		retry: 1,
	});

	return { ...query, dateError, period };
};
